import { FC, memo, useMemo } from "react";

import { AvatarSmart } from "@/components/_atoms/avatar-smart";
import { cn } from "@/lib/cn";

import { useCurrentUserContext } from "../hooks/use-current-user-context";
import { User } from "../models/user-model";

export interface UserAvatarProps {
  user?: User;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  showBorder?: boolean;
  showStatus?: boolean;
  status?: "online" | "offline" | "away";
}

const AVATAR_FALLBACK_BACKGROUND_COLOR = "rgb(154 94 0)";

const statusClasses = {
  online: "bg-green-500",
  offline: "bg-slate-400",
  away: "bg-yellow-500",
};

export const UserAvatar: FC<UserAvatarProps> = memo(
  ({ className, user, size = "sm", showBorder = false, showStatus = false, status = "offline" }) => {
    const { currentUser } = useCurrentUserContext();

    const selectedUser = useMemo(() => {
      return user ?? currentUser;
    }, [user, currentUser]);

    const fallbackInitials = useMemo(() => {
      if (!selectedUser) return "";
      if (selectedUser.fullname) return selectedUser.fullname[0].toUpperCase();
      if (selectedUser.username) return selectedUser.username[0].toUpperCase();
      return selectedUser.email[0].toUpperCase();
    }, [selectedUser]);

    if (!selectedUser) return null;

    return (
      <div className="relative">
        <AvatarSmart
          src={selectedUser.profilePicture || undefined}
          fallback={
            <div
              className="flex h-full w-full items-center justify-center font-semibold text-white"
              style={{ backgroundColor: AVATAR_FALLBACK_BACKGROUND_COLOR }}
            >
              {fallbackInitials}
            </div>
          }
          className={className}
          size={size}
          shape="rounded"
          showBorder={showBorder}
        />

        {showStatus && (
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background",
              statusClasses[status],
            )}
          />
        )}
      </div>
    );
  },
);

UserAvatar.displayName = "UserAvatar";
